<template>
    <Modal ref="modal" class="add-missing-products-with-subcustomers-modal" @closed="onModalClose">
        <template #header="sp">
            <div class="h3">Marquer des produits manquants</div>
        </template>
        <template #body="sp">
            <div class="form-group row">
                <label for="staticCustomerName" class="col-4 col-form-label">{{ customerTypeLabel }}</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticCustomerName" :value="order.customer.name">
                </div>
            </div>

            <div class="form-group row">
                <label for="staticDeliveryDate" class="col-4 col-form-label">Date de livraison</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticDeliveryDate" :value="formatDate(order.delivery_date)">
                </div>
            </div>

            <div class="form-group row">
                <label for="staticProductName" class="col-4 col-form-label">Produit</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticProductName" :value="productName">
                </div>
            </div>

            <div class="form-group row">
                <label for="reason" class="col-4 col-form-label">Raison</label>
                <div class="col-8">
                    <select class="form-control" id="reason" v-model="reasonId">
                        <option v-for="reason in cancellationReasons" :value="reason.id">{{ reason.reason }}</option>
                    </select>
                </div>
            </div>

            <div class="h5 mt-4">Cocher une case par produit manquant</div>

            <table class="table">
                <template v-for="orderedProduct in orderedProducts">
                    <tr v-for="idx in orderedProduct.quantity">
                        <td>
                            <input
                                v-if="isProductAlreadyMissing(orderedProduct.subcustomer.id, idx)"
                                type="checkbox"
                                class="missing-checkbox"
                                :id="orderedProduct.ref + '_' + idx"
                                checked
                                disabled
                            >
                            <input
                                v-else
                                type="checkbox"
                                class="missing-checkbox"
                                :id="orderedProduct.ref + '_' + idx"
                                :value="{ fk_subcustomer: orderedProduct.subcustomer.id, index: idx }"
                                v-model="missingProductsChecked">
                        </td>
                        <td v-if="orderedProduct.ref && !orderedProduct.ref.startsWith('(')">
                            n°{{ orderedProduct.ref }}
                        </td>
                        <td>
                            {{ orderedProduct.subcustomer.name }}
                        </td>
                    </tr>
                </template>
            </table>

            <template v-if="order.paid_at">
                <div v-if="order.online" class="alert alert-warning">
                    Cette commande click & collect étant payée d'avance, vous devrez rembourser le client du montant correspondant aux produits manquants.
                </div>
                <div v-else-if="mustConfirmRefund && !refundConfirmed" class="alert alert-danger">
                    Cette commande étant payée d'avance, vous devrez rembourser le client du montant correspondant aux produits manquants.

                    <button class="btn btn-danger btn-block" @click="refundConfirmed = true">J'ai compris</button>
                </div>
            </template>

            <ErrorsZone :errors="errors" />
        </template>
        <template #footer>
            <div class="font-weight-bold mb-2">
                Total de produits manquants après validation : {{ missingProductsChecked.length + missingProductsPreChecked.length }}
            </div>

            <AuthorizationModal
                token-type="AddMissingProductsForSubcustomers"
                :token-payload="authorizationTokenPayload"
                @authorised="onAuthorizationModalAuthorize"
            >
                <template #button="sp">
                    <AuthorizationModalButton :disabled="missingProductsChecked.length < 1 || (mustConfirmRefund && !refundConfirmed)" class="btn-warning" @click="onClickAddButton(sp)">
                        Marquer <b>{{ missingProductsChecked.length }}</b> nouveaux produits manquants
                    </AuthorizationModalButton>
                </template>
            </AuthorizationModal>
        </template>
    </Modal>
</template>

<script>
    import Modal from "../Modal";
    import ErrorsZone from "../ErrorsZone";
    import AuthorizationModal from "../AuthorizationModal";
    import AuthorizationModalButton from "../AuthorizationModalButton";

    import {usePostRequest} from "../compositions/axios/postRequest";

    export default {
        name: 'AddMissingProductsWithSubcustomersModal',

        components: {
            Modal,
            ErrorsZone,
            AuthorizationModal,
            AuthorizationModalButton,
        },

        setup() {
            const {
                loading: sending,
                errors,
                post
            } = usePostRequest();

            return {
                sending,
                errors,
                post
            };
        },

        props: {
            productSku: {
                type: String,
                required: true,
            },
            productName: {
                type: String,
                required: true,
            },
            order: {
                type: Object,
                required: true,
            },
            subcustomersOrders: {
                type: Array,
                required: true,
            },
            cancellationReasons: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                reasonId: null,

                refundConfirmed: false,

                missingProductsChecked: [],
            };
        },

        methods: {
            isProductAlreadyMissing(fkSubcustomer, index) {
                for (const mpc of this.missingProductsPreChecked) {
                    if (mpc.fk_subcustomer == fkSubcustomer && index <= mpc.index) {
                        return true;
                    }
                }
                return false;
            },

            showModal() {
                this.$refs.modal.showModal();
            },

            closeModal() {
                this.$refs.modal.closeModal();
            },

            formatDate(date) {
                return moment(date).format('dddd D MMMM Y')
            },

            onModalClose() {
                this.reasonId = null;
                this.refundConfirmed = false;
                this.errors = null;
            },

            onClickAddButton(sp) {
                if (this.validateFields()) {
                    sp.showModal();
                }
            },

            validateFields() {
                this.errors = null;

                let errors = {};

                if (!this.reasonId) {
                    errors['reason'] = ["La raison est obligatoire."];
                }

                if (Object.keys(errors).length > 0) {
                    this.errors = { message: "Merci de corriger les erreurs suivantes :", errors: errors };
                    return false;
                }

                return true;
            },

            async onAuthorizationModalAuthorize(token) {
                const response = await this.post('/espace-employes/commandes/preparation/commande/' + this.order.id + '/produits-manquants-clients', {
                    sku: this.productSku,
                    quantities: this.missingProductsToSubmit,
                    reason: this.reasonId,
                    authorization_token: token ? token.token : null,
                });

                if (!this.errors) {
                    this.missingProductsChecked = [];
                    this.closeModal();

                    if (!this.$root.echoConnectedWihoutErrors) {
                        window.location.reload();
                    }
                }
            },
        },

        computed: {
            missingProductsPreChecked() {
                let custQuantities = {};
                for (const p of this.order.missing_products) {
                    if (p.sku == this.productSku) {
                        custQuantities[p.pivot.fk_subcustomer] = (custQuantities[p.pivot.fk_subcustomer] || 0) + p.pivot.quantity;
                    }
                }

                let missingProductsPreChecked = [];
                for (const custId in custQuantities) {
                    for (let i = 1; i <= custQuantities[custId]; i++) {
                        missingProductsPreChecked.push({
                            fk_subcustomer: custId,
                            index: i,
                        });
                    }
                }
                return missingProductsPreChecked;
            },

            missingProductsToSubmit() {
                return this.missingProductsChecked.reduce((acc, item) => {
                    acc[item.fk_subcustomer] = (acc[item.fk_subcustomer] || 0) + 1;
                    return acc;
                }, {});
            },

            orderedProducts() {
                return this.order.ordered_products
                    .filter((item) => {
                        return item.sku == this.productSku;
                    })
                    .map((item) => {
                        const subcustomer = this.subcustomersOrders.find(cust => cust.subcustomer.id == item.pivot.fk_subcustomer);
                        return {
                            subcustomer: subcustomer.subcustomer,
                            ref: item.pivot.ref,
                            sku: item.sku,
                            quantity: item.pivot.quantity,
                        };
                    });
            },

            authorizationTokenPayload() {
                return {
                    order_id: this.order.id,
                    product_id: this.productSku,
                    quantities: this.missingProductsToSubmit,
                    reason_id: this.reasonId,
                };
            },

            mustConfirmRefund() {
                return this.order.paid_at && !this.order.online;
            },

            customerTypeLabel() {
                switch (this.order.customer.type) {
                    case 'store':
                        return 'Magasin';
                    case 'short_channel':
                        return 'Circuit court';
                    case 'customer_group':
                        return 'Groupement de clients';
                    case 'pickup_point':
                        return 'Lieu de retrait';
                    default:
                        return 'Client';
                }
            },
        },
    }
</script>

<style lang="scss">
    .add-missing-products-with-subcustomers-modal {
        :first-of-type(.modal-container) {
            max-width: 800px;
            width: auto;
        }

        .missing-checkbox {
            width: 20px;
            height: 20px;
        }
    }
</style>
